import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useParams, NavLink, useNavigate } from "react-router-dom";
import { FaPhoneAlt, FaEnvelopeOpen, FaTimes } from "react-icons/fa";
import { FiPlus, FiMenu, FiFilter } from "react-icons/fi";
import CreateBillPopup from "./CreateBillPopup";
import SupplierExpensePopup from "./SupplierExpensePopup";
import axios from "axios";
import "./SuppliersDetailPage.css";
import { useCurrency } from '../currency/currencyContext'; // Add currency context

// Date formatting utility functions
const formatDateToDDMMYYYY = (dateString) => {
  if (!dateString) return "";

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
  } catch (error) {
    console.error("Error formatting date:", error);
    return dateString;
  }
};

const parseDDMMYYYYToDate = (dateString) => {
  if (!dateString) return "";

  try {
    const [day, month, year] = dateString.split('-');
    return `${year}-${month}-${day}`;
  } catch (error) {
    console.error("Error parsing date:", error);
    return dateString;
  }
};

// Define currentDate outside the component to ensure stability
const currentDate = new Date();

// Define getCurrentDate function
const getCurrentDate = () => {
  const now = new Date();
  const day = String(now.getDate()).padStart(2, '0');
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const year = now.getFullYear();
  return `${day}-${month}-${year}`;
};

// Get current date in YYYY-MM-DD format for input fields
const getCurrentDateForInput = () => {
  return new Date().toISOString().split('T')[0];
};

const SuppliersDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [supplierData, setSupplierData] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortOrder, setSortOrder] = useState("name-asc");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState({
    openBalance: "0.00",
    overdue: "0.00",
    paidLast30Days: "0.00",
  });
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [showCreateBillPopup, setShowCreateBillPopup] = useState(false);
  const [showCreateExpensePopup, setShowCreateExpensePopup] = useState(false);
  const [selectedBill, setSelectedBill] = useState(null);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [expenseTableData, setExpenseTableData] = useState([
    {
      id: Date.now(),
      rowNumber: "1",
      category: "General",
      description: "Initial Item",
      totalAmount: "",
    },
  ]);
  const [expenseFile, setExpenseFile] = useState(null);
  const [paymentAccount, setPaymentAccount] = useState("Cash on hand");
  const [paymentDate, setPaymentDate] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [transactionType, setTransactionType] = useState("All transactions");
  const [selectedTransactionIds, setSelectedTransactionIds] = useState([]);
  const [showChangeCategoryModal, setShowChangeCategoryModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [newCategory, setNewCategory] = useState("");
  const [recentlyDeletedBillIds, setRecentlyDeletedBillIds] = useState([]);
  const dropdownRef = useRef(null);
  const filterBtnRef = useRef(null);
  const filterModalRef = useRef(null);
  const changeCategoryModalRef = useRef(null);
  const deleteModalRef = useRef(null);
  const [showSupplierDetails, setShowSupplierDetails] = useState(false);
  const [dateFilter, setDateFilter] = useState("All dates");
  const [customFromDate, setCustomFromDate] = useState("");
  const [customToDate, setCustomToDate] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [amountOperator, setAmountOperator] = useState("Between");
  const [minAmount, setMinAmount] = useState("");
  const [maxAmount, setMaxAmount] = useState("");
  const API_BASE_URL = "https://backend.hira-tech.co.uk";
  const [categories, setCategories] = useState([]);
  const [categoryMap, setCategoryMap] = useState({});
  const { formatPrice, getCurrencySymbol, convertPrice, getCurrentCurrency } = useCurrency(); // Add currency context

  // Fetch categories from backend
  useEffect(() => {
    setLoading(true);
    fetch(`${API_BASE_URL}/api/categories/`, {
      headers: { Accept: "application/json" },
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Categories fetch error! Status: ${response.status}`);
        return response.json();
      })
      .then((data) => {
        setCategories(data);
        const map = data.reduce((acc, cat) => {
          acc[cat.name] = cat.display_name;
          return acc;
        }, {});
        setCategoryMap(map);
        setError(null);
      })
      .catch((error) => {
        console.error(`Failed to fetch categories: ${error.message}`);
        setError(`Failed to fetch categories: ${error.message}`);
      })
      .finally(() => setLoading(false));
  }, []);

  // Handle outside click to close dropdowns and modals
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
      if (
        filterModalRef.current &&
        !filterModalRef.current.contains(event.target) &&
        filterBtnRef.current &&
        !filterBtnRef.current.contains(event.target)
      ) {
        setShowFilterModal(false);
      }
      if (
        changeCategoryModalRef.current &&
        !changeCategoryModalRef.current.contains(event.target)
      ) {
        setShowChangeCategoryModal(false);
        setNewCategory("");
      }
      if (
        deleteModalRef.current &&
        !deleteModalRef.current.contains(event.target)
      ) {
        setShowDeleteModal(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch suppliers
  useEffect(() => {
    setLoading(true);
    fetch(`${API_BASE_URL}/api/suppliers/?search=${encodeURIComponent(searchQuery)}`, {
      headers: { Accept: "application/json" },
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Suppliers fetch error! Status: ${response.status}`);
        return response.json();
      })
      .then((data) => {
        const sortedData = [...data].sort((a, b) => {
          const nameA = a.supplier_display_name || `${a.first_name} ${a.last_name}`;
          const nameB = b.supplier_display_name || `${b.first_name} ${b.last_name}`;
          return sortOrder === "name-asc" ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
        });
        setSuppliers(sortedData);
        setError(null);
      })
      .catch((error) => {
        setError(`Failed to fetch suppliers: ${error.message}`);
      })
      .finally(() => setLoading(false));
  }, [searchQuery, sortOrder]);

  // Fetch supplier details
  useEffect(() => {
    if (!id) return;
    setLoading(true);
    fetch(`${API_BASE_URL}/api/suppliers/${id}/`, {
      headers: { Accept: "application/json" },
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Supplier fetch error! Status: ${response.status}`);
        return response.json();
      })
      .then((data) => {
        setSupplierData({
          ...data,
          name: data.supplier_display_name || `${data.first_name} ${data.last_name}`,
          balance: data.balance ? parseFloat(data.balance).toFixed(2) : "0.00",
        });
        setError(null);
      })
      .catch((error) => {
        setError(`Failed to fetch supplier details: ${error.message}`);
      })
      .finally(() => setLoading(false));
  }, [id]);

  // Fetch supplier bills and expenses
  useEffect(() => {
    if (!id || !supplierData) return;
    setLoading(true);
    const fetchBills = fetch(`${API_BASE_URL}/api/suppliers/${id}/bills/`, {
      headers: { Accept: "application/json" },
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Bills fetch error! Status: ${response.status}`);
        return response.json();
      })
      .then((data) => {
        console.log("Bills API response:", data);
        return data;
      });
    const fetchExpenses = fetch(`${API_BASE_URL}/api/suppliers/${id}/expenses/`, {
      headers: { Accept: "application/json" },
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Expenses fetch error! Status: ${response.status}`);
        return response.json();
      });
    Promise.all([fetchBills, fetchExpenses])
      .then(([bills, expenses]) => {
        const mappedBills = bills
          .filter((bill) => !bill.deleted_from_supplier && !recentlyDeletedBillIds.includes(bill.id))
          .map((bill) => {
            const billAmount = parseFloat(bill.amount || bill.total || 0);
            const categoryValue = bill.terms && categoryMap[bill.terms] ? categoryMap[bill.terms] : bill.terms || "Uncategorized";
            console.log(`Supplier Bill ID ${bill.id} - terms: ${bill.terms}, mapped category: ${categoryValue}, raw API data:`, bill);
            return {
              id: Number(bill.id),
              date: formatDateToDDMMYYYY(bill.date) || getCurrentDate(), // Format date to DD-MM-YYYY
              type: "Bill",
              no: bill.bill_number || `BILL-${bill.id}`,
              payee: supplierData?.name || bill.supplier_name || "Unknown",
              category: categoryValue,
              terms: bill.terms || "",
              totalBeforeTax: billAmount ? (billAmount / 1.17).toFixed(2) : "0.00",
              salesTax: billAmount ? (billAmount * 0.17 / 1.17).toFixed(2) : "0.00",
              total: billAmount,
              status: bill.status || (bill.paid_date ? "Paid" : "Pending"),
              address: bill.address || "No address",
              start_date: formatDateToDDMMYYYY(bill.start_date) || "", // Format date to DD-MM-YYYY
              end_date: formatDateToDDMMYYYY(bill.end_date) || "", // Format date to DD-MM-YYYY
              memo: bill.memo || "No memo",
              supplier: bill.supplier || id,
              bill_number: bill.bill_number || `BILL-${bill.id}`,
              due_date: formatDateToDDMMYYYY(bill.due_date) || getCurrentDate(), // Format date to DD-MM-YYYY
              file_name: bill.bill_pdf ? bill.bill_pdf.split("/").pop() : "Manual Entry",
              file_path: bill.bill_pdf ? `${API_BASE_URL}${bill.bill_pdf}` : "",
              items: bill.items
                ? bill.items.map((item) => ({
                    id: Date.now() + Math.random(),
                    rowNumber: item.rowNumber || "1",
                    category: item.category && categoryMap[item.category] ? categoryMap[item.category] : item.category || categoryValue,
                    description: item.description || "Initial Item",
                    totalAmount: parseFloat(item.totalAmount || billAmount || 0),
                  }))
                : [
                    {
                      id: Date.now() + Math.random(),
                      rowNumber: "1",
                      category: categoryValue,
                      description: bill.memo || "Initial Item",
                      totalAmount: billAmount,
                    },
                  ],
            };
          });
        const mappedExpenses = expenses.map((expense) => {
          const expenseAmount = parseFloat(expense.amount || expense.total || 0);
          const categoryValue = categoryMap[expense.terms] || expense.terms || "General";
          return {
            id: expense.id,
            date: formatDateToDDMMYYYY(expense.date) || "", // Format date to DD-MM-YYYY
            type: "Expense",
            no: expense.no || expense.expense_number || `EXP-${expense.id}`,
            payee: supplierData?.name || expense.supplier || "Unknown",
            category: categoryValue,
            totalBeforeTax: expenseAmount ? (expenseAmount / 1.17).toFixed(2) : "0.00",
            salesTax: expenseAmount ? (expenseAmount * 0.17 / 1.17).toFixed(2) : "0.00",
            total: expenseAmount,
            status: "Paid", // Set default status to "Paid" for expenses
            address: expense.address || "",
            start_date: "",
            end_date: "",
            memo: expense.memo || "",
            supplier: expense.supplier || id,
            expense_number: expense.no || expense.expense_number || `EXP-${expense.id}`,
            due_date: formatDateToDDMMYYYY(expense.due_date) || "", // Format date to DD-MM-YYYY
            file_name: expense.file_path ? expense.file_path.split("/").pop() : "Manual Entry",
            file_path: expense.file_path ? `${API_BASE_URL}${expense.file_path}` : "",
            method: expense.method || "",
            paymentAccount: expense.paymentAccount || "Cash on hand",
            items: expense.items
              ? expense.items.map((item) => ({
                  id: Date.now() + Math.random(),
                  rowNumber: item.rowNumber || "1",
                  category: categoryMap[item.category] || item.category || "General",
                  description: item.description || "Initial Item",
                  totalAmount: parseFloat(item.totalAmount || expenseAmount || 0),
                }))
              : [
                  {
                    id: Date.now() + Math.random(),
                    rowNumber: "1",
                    category: categoryValue,
                    description: expense.memo || "Initial Item",
                    totalAmount: expenseAmount,
                  },
                ],
          };
        });
        setTransactions([...mappedBills, ...mappedExpenses]);
        setError(null);
      })
      .catch((error) => {
        setError(`Failed to fetch transactions: ${error.message}`);
      })
      .finally(() => setLoading(false));
  }, [id, supplierData, recentlyDeletedBillIds, categoryMap]);

  // Calculate summary metrics (only for bills)
  useEffect(() => {
    const currentMonthStart = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    const thirtyDaysAgo = new Date(currentDate);
    thirtyDaysAgo.setDate(currentDate.getDate() - 30);
    let openBalance = 0;
    let overdue = 0;
    let paidLast30Days = 0;
    transactions
      .filter((trans) => trans.type === "Bill")
      .forEach((transaction) => {
        const amount = parseFloat(transaction.total) || 0;
        // Parse DD-MM-YYYY format for date comparison
        const dueDate = transaction.due_date ? parseDDMMYYYYToDate(transaction.due_date) : null;
        const transDate = transaction.date ? parseDDMMYYYYToDate(transaction.date) : null;
        const parsedDueDate = dueDate ? new Date(dueDate) : null;
        const parsedTransDate = transDate ? new Date(transDate) : null;

        if (["Pending", "Unpaid"].includes(transaction.status)) {
          openBalance += amount;
          if (parsedDueDate && parsedDueDate < currentMonthStart) overdue += amount;
        }
        if (transaction.status === "Paid" && parsedTransDate && parsedTransDate >= thirtyDaysAgo) {
          paidLast30Days += amount;
        }
      });
    setSummary({
      openBalance: openBalance.toFixed(2),
      overdue: overdue.toFixed(2),
      paidLast30Days: paidLast30Days.toFixed(2),
    });
  }, [transactions]);

  // Handle checkbox selection for transactions
  const handleCheckboxChange = (transactionId) => {
    setSelectedTransactionIds((prev) =>
      prev.includes(transactionId)
        ? prev.filter((id) => id !== transactionId)
        : [...prev, transactionId]
    );
  };

  // Check if only bills are selected
  const onlyBillsSelected = () =>
    selectedTransactionIds.length > 0 &&
    selectedTransactionIds.every((id) => {
      const transaction = filteredTransactions.find((t) => t.id === id);
      return transaction && transaction.type === "Bill";
    });

  // Open Change Category modal with pre-selected category
  const handleOpenChangeCategoryModal = async () => {
    if (selectedTransactionIds.length === 0) {
      setError("Please select at least one bill.");
      alert("Please select at least one bill.");
      return;
    }
    if (!onlyBillsSelected()) {
      setError("Please select only bills for category change.");
      alert("Please select only bills for category change.");
      return;
    }
    try {
      setLoading(true);
      const firstBillId = selectedTransactionIds[0];
      const response = await axios.get(`${API_BASE_URL}/api/bills/${firstBillId}/`, {
        headers: { Accept: "application/json" },
      });
      const billData = response.data;
      const categoryKey = billData.terms && categoryMap[billData.terms] ? billData.terms : "General";
      setNewCategory(categoryKey);
      setShowChangeCategoryModal(true);
      setError(null);
    } catch (error) {
      setError(`Failed to fetch bill details: ${error.message}`);
      alert(`Failed to fetch bill details: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle change category
  const handleChangeCategory = async () => {
    if (!newCategory) {
      setError("Please select a category.");
      alert("Please select a category.");
      return;
    }
    if (selectedTransactionIds.length === 0) {
      setError("Please select at least one bill.");
      alert("Please select at least one bill.");
      return;
    }
    if (!onlyBillsSelected()) {
      setError("Please select only bills for category change.");
      alert("Please select only bills for category change.");
      return;
    }
    try {
      setLoading(true);
      const promises = selectedTransactionIds
        .map(async (id) => {
          const transaction = transactions.find((t) => t.id === id);
          if (transaction && transaction.type === "Bill") {
            const response = await axios.get(`${API_BASE_URL}/api/bills/${id}/`, {
              headers: { Accept: "application/json" },
            });
            const currentBill = response.data;
            console.log(`Changing category for Bill ID ${id} - current terms: ${currentBill.terms}, new terms: ${newCategory}`);
            const updatedBillData = {
              ...currentBill,
              terms: newCategory,
              amount: parseFloat(currentBill.amount || 0),
              items: currentBill.items
                ? currentBill.items.map((item) => ({
                    ...item,
                    category: newCategory,
                    totalAmount: parseFloat(item.totalAmount || 0),
                  }))
                : currentBill.items,
            };
            return axios
              .put(`${API_BASE_URL}/api/bills/${id}/`, updatedBillData, {
                headers: { "Content-Type": "application/json" },
              })
              .then((response) => {
                const updatedBill = response.data;
                const billAmount = parseFloat(updatedBill.amount || updatedBill.total || 0);
                const categoryValue = updatedBill.terms && categoryMap[updatedBill.terms] ? categoryMap[updatedBill.terms] : updatedBill.terms || "Uncategorized";
                console.log(`Updated Bill ID ${updatedBill.id} - terms: ${updatedBill.terms}, mapped category: ${categoryValue}`);
                return {
                  id: updatedBill.id,
                  date: formatDateToDDMMYYYY(updatedBill.date) || getCurrentDate(), // Format date to DD-MM-YYYY
                  type: "Bill",
                  no: updatedBill.bill_number || `BILL-${updatedBill.id}`,
                  payee: supplierData?.name || updatedBill.supplier_name || "Unknown",
                  category: categoryValue,
                  terms: updatedBill.terms || "",
                  totalBeforeTax: billAmount ? (billAmount / 1.17).toFixed(2) : "0.00",
                  salesTax: billAmount ? (billAmount * 0.17 / 1.17).toFixed(2) : "0.00",
                  total: billAmount,
                  status: updatedBill.status || (updatedBill.paid_date ? "Paid" : "Pending"),
                  address: updatedBill.address || "No address",
                  start_date: formatDateToDDMMYYYY(updatedBill.start_date) || "", // Format date to DD-MM-YYYY
                  end_date: formatDateToDDMMYYYY(updatedBill.end_date) || "", // Format date to DD-MM-YYYY
                  memo: updatedBill.memo || "No memo",
                  supplier: updatedBill.supplier || id,
                  bill_number: updatedBill.bill_number || `BILL-${updatedBill.id}`,
                  due_date: formatDateToDDMMYYYY(updatedBill.due_date) || getCurrentDate(), // Format date to DD-MM-YYYY
                  file_name: updatedBill.bill_pdf ? updatedBill.bill_pdf.split("/").pop() : "Manual Entry",
                  file_path: updatedBill.bill_pdf ? `${API_BASE_URL}${updatedBill.bill_pdf}` : "",
                  items: updatedBill.items
                    ? updatedBill.items.map((item) => ({
                        id: Date.now() + Math.random(),
                        rowNumber: item.rowNumber || "1",
                        category: item.category && categoryMap[item.category] ? categoryMap[item.category] : item.category || categoryValue,
                        description: item.description || "Initial Item",
                        totalAmount: parseFloat(item.totalAmount || billAmount || 0),
                      }))
                    : [
                        {
                          id: Date.now() + Math.random(),
                          rowNumber: "1",
                          category: categoryValue,
                          description: updatedBill.memo || "Initial Item",
                          totalAmount: billAmount,
                        },
                      ],
                };
              });
          }
          return null;
        })
        .filter((promise) => promise !== null);
      if (promises.length === 0) {
        setError("No bills selected for category change.");
        alert("No bills selected for category change.");
        return;
      }
      const updatedBills = await Promise.all(promises);
      setTransactions((prev) =>
        prev.map((trans) => {
          const updatedBill = updatedBills.find((bill) => bill.id === trans.id);
          return updatedBill || trans;
        })
      );
      setShowChangeCategoryModal(false);
      setSelectedTransactionIds([]);
      setNewCategory("");
      setError(null);
      alert(`Successfully updated category to "${categoryMap[newCategory] || newCategory}" for ${promises.length} bill(s).`);
    } catch (error) {
      setError(`Failed to update categories: ${error.message}`);
      alert(`Failed to update categories: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBill = async () => {
    try {
      setLoading(true);
      const promises = selectedTransactionIds
        .map((id) => {
          const transaction = filteredTransactions.find((t) => t.id === id);
          if (transaction && transaction.type === "Bill") {
            return axios.patch(`${API_BASE_URL}/api/bills/${id}/mark-for-review/`, {
              needs_review: true,
              deleted_from_supplier: true,
              is_deleted: false,
            });
          }
          return null;
        })
        .filter((promise) => promise !== null);
      if (promises.length === 0) {
        alert("No bills selected for deletion.");
        return;
      }
      await Promise.all(promises);
      setRecentlyDeletedBillIds((prev) => [
        ...prev,
        ...selectedTransactionIds.filter((id) =>
          filteredTransactions.find((t) => t.id === id && t.type === "Bill")
        ),
      ]);
      setTransactions((prev) =>
        prev.filter(
          (trans) => !(trans.type === "Bill" && selectedTransactionIds.includes(trans.id))
        )
      );
      setSelectedTransactionIds([]);
      setShowDeleteModal(false);
      alert("Bill(s) marked for review and removed from supplier page!");
    } catch (error) {
      setError(`Failed to mark bills for review: ${error.message}`);
      alert(`Failed to mark bills for review: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Filter transactions
  const filterTransactions = useCallback(() => {
    let filtered = [...transactions];
    if (transactionType !== "All transactions") {
      filtered = filtered.filter((trans) => trans.type === transactionType);
    }
    if (dateFilter !== "All dates") {
      if (dateFilter === "Custom" && customFromDate && customToDate) {
        const fromDate = new Date(customFromDate);
        const toDate = new Date(customToDate);
        toDate.setHours(23, 59, 59, 999);
        filtered = filtered.filter((trans) => {
          // Parse DD-MM-YYYY format for filtering
          const transDateStr = trans.date ? parseDDMMYYYYToDate(trans.date) : null;
          const transDate = transDateStr ? new Date(transDateStr) : null;
          return transDate && !isNaN(transDate) && transDate >= fromDate && transDate <= toDate;
        });
      } else if (dateFilter === "Since 365 Days ago") {
        const oneYearAgo = new Date(currentDate);
        oneYearAgo.setFullYear(currentDate.getFullYear() - 1);
        filtered = filtered.filter((trans) => {
          const transDateStr = trans.date ? parseDDMMYYYYToDate(trans.date) : null;
          const transDate = transDateStr ? new Date(transDateStr) : null;
          return transDate && !isNaN(transDate) && transDate >= oneYearAgo;
        });
      } else if (dateFilter === "This Month") {
        const thisMonthStart = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
        filtered = filtered.filter((trans) => {
          const transDateStr = trans.date ? parseDDMMYYYYToDate(trans.date) : null;
          const transDate = transDateStr ? new Date(transDateStr) : null;
          return transDate && !isNaN(transDate) && transDate >= thisMonthStart;
        });
      } else if (dateFilter === "This Quarter") {
        const quarterStartMonth = Math.floor(currentDate.getMonth() / 3) * 3;
        const quarterStart = new Date(currentDate.getFullYear(), quarterStartMonth, 1);
        filtered = filtered.filter((trans) => {
          const transDateStr = trans.date ? parseDDMMYYYYToDate(trans.date) : null;
          const transDate = transDateStr ? new Date(transDateStr) : null;
          return transDate && !isNaN(transDate) && transDate >= quarterStart;
        });
      } else if (dateFilter === "This Year") {
        const thisYearStart = new Date(currentDate.getFullYear(), 0, 1);
        filtered = filtered.filter((trans) => {
          const transDateStr = trans.date ? parseDDMMYYYYToDate(trans.date) : null;
          const transDate = transDateStr ? new Date(transDateStr) : null;
          return transDate && !isNaN(transDate) && transDate >= thisYearStart;
        });
      } else if (dateFilter === "Last Month") {
        const lastMonthStart = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
        const lastMonthEnd = new Date(currentDate.getFullYear(), currentDate.getMonth(), 0);
        filtered = filtered.filter((trans) => {
          const transDateStr = trans.date ? parseDDMMYYYYToDate(trans.date) : null;
          const transDate = transDateStr ? new Date(transDateStr) : null;
          return transDate && !isNaN(transDate) && transDate >= lastMonthStart && transDate <= lastMonthEnd;
        });
      } else if (dateFilter === "Last Quarter") {
        const quarterStartMonth = Math.floor((currentDate.getMonth() - 3) / 3) * 3;
        const quarterStart = new Date(currentDate.getFullYear(), quarterStartMonth, 1);
        const quarterEnd = new Date(currentDate.getFullYear(), quarterStartMonth + 2, 0);
        filtered = filtered.filter((trans) => {
          const transDateStr = trans.date ? parseDDMMYYYYToDate(trans.date) : null;
          const transDate = transDateStr ? new Date(transDateStr) : null;
          return transDate && !isNaN(transDate) && transDate >= quarterStart && transDate <= quarterEnd;
        });
      } else if (dateFilter === "Last Year") {
        const lastYearStart = new Date(currentDate.getFullYear() - 1, 0, 1);
        const lastYearEnd = new Date(currentDate.getFullYear() - 1, 11, 31);
        filtered = filtered.filter((trans) => {
          const transDateStr = trans.date ? parseDDMMYYYYToDate(trans.date) : null;
          const transDate = transDateStr ? new Date(transDateStr) : null;
          return transDate && !isNaN(transDate) && transDate >= lastYearStart && transDate <= lastYearEnd;
        });
      }
    }
    if (categoryFilter) {
      filtered = filtered.filter((trans) => trans.category === categoryFilter);
    }
    if (minAmount || maxAmount) {
      filtered = filtered.filter((trans) => {
        const amount = parseFloat(trans.total) || 0;
        if (amountOperator === "Between") {
          const min = minAmount ? parseFloat(minAmount) : -Infinity;
          const max = maxAmount ? parseFloat(maxAmount) : Infinity;
          return amount >= min && amount <= max;
        } else if (amountOperator === "Less than") {
          return amount <= (parseFloat(minAmount) || 0);
        } else if (amountOperator === "Greater than") {
          return amount >= (parseFloat(minAmount) || 0);
        } else if (amountOperator === "Equals") {
          return amount === (parseFloat(minAmount) || 0);
        }
        return true;
      });
    }
    return filtered;
  }, [
    transactions,
    transactionType,
    dateFilter,
    customFromDate,
    customToDate,
    categoryFilter,
    amountOperator,
    minAmount,
    maxAmount,
  ]);

  // Apply filters
  const filteredTransactionsMemo = useMemo(() => filterTransactions(), [filterTransactions]);
  useEffect(() => {
    setFilteredTransactions(filteredTransactionsMemo);
  }, [filteredTransactionsMemo]);

  const handleResetFilters = () => {
    setDateFilter("All dates");
    setCustomFromDate("");
    setCustomToDate("");
    setCategoryFilter("");
    setAmountOperator("Between");
    setMinAmount("");
    setMaxAmount("");
    setTransactionType("All transactions");
  };

  const handleCreateBillClick = () => {
    setSelectedBill(null);
    setSelectedExpense(null);
    setShowCreateBillPopup(true);
    setShowCreateExpensePopup(false);
    setIsDropdownOpen(false);
  };

  const handleCreateExpenseClick = () => {
    setSelectedExpense(null);
    setSelectedBill(null);
    setPaymentDate(getCurrentDate());
    setPaymentMethod("");
    setDescription("");
    setCategory("");
    setExpenseFile(null);
    setExpenseTableData([
      {
        id: Date.now(),
        rowNumber: "1",
        category: "General",
        description: "Initial Item",
        totalAmount: "",
      },
    ]);
    setShowCreateExpensePopup(true);
    setShowCreateBillPopup(false);
    setIsDropdownOpen(false);
  };

  const handleTransactionClick = (transaction) => {
    setShowCreateBillPopup(false);
    setShowCreateExpensePopup(false);
    setSelectedBill(null);
    setSelectedExpense(null);
    if (transaction.type === "Bill") {
      setSelectedBill({
        ...transaction,
        total: parseFloat(transaction.total || 0),
        totalBeforeTax: parseFloat(transaction.totalBeforeTax || 0),
        salesTax: parseFloat(transaction.salesTax || 0),
        items: transaction.items.map((item) => ({
          ...item,
          totalAmount: parseFloat(item.totalAmount || 0),
        })),
      });
      setShowCreateBillPopup(true);
    } else if (transaction.type === "Expense") {
      const expenseItems = transaction.items && Array.isArray(transaction.items)
        ? transaction.items.map((item) => ({
            id: Date.now() + Math.random(),
            rowNumber: item.rowNumber,
            category: categoryMap[item.category] || item.category || "General",
            description: item.description || "Initial Item",
            totalAmount: parseFloat(item.totalAmount || 0),
          }))
        : [
            {
              id: Date.now() + Math.random(),
              rowNumber: "1",
              category: categoryMap[transaction.category] || transaction.category || "General",
              description: transaction.memo || "Initial Item",
              totalAmount: parseFloat(transaction.total || 0),
            },
          ];
      const expenseData = {
        id: transaction.id,
        date: transaction.date || "",
        expense_number: transaction.no || `EXP-${transaction.id}`,
        supplier: transaction.supplier || id,
        payee: supplierData?.name || transaction.payee || "Unknown",
        terms: transaction.category || "General",
        memo: transaction.memo || "",
        total: parseFloat(transaction.total || 0),
        file_name: transaction.file_name || "Manual Entry",
        file_path: transaction.file_path || "",
        method: transaction.method || "",
        paymentAccount: transaction.paymentAccount || "Cash on hand",
        items: expenseItems,
        status: "Paid", // Ensure expense status is "Paid"
      };
      setSelectedExpense(expenseData);
      setPaymentDate(transaction.date || getCurrentDate());
      setPaymentMethod(transaction.method || "");
      setDescription(transaction.memo || "");
      setCategory(categoryMap[transaction.category] || transaction.category || "General");
      setExpenseFile(
        transaction.file_name && transaction.file_name !== "Manual Entry"
          ? { name: transaction.file_name }
          : null
      );
      setExpenseTableData(expenseItems);
      setShowCreateExpensePopup(true);
    }
  };

  const handleBillCreated = (newBill) => {
    console.log("Creating bill - input data:", newBill);
    const billAmount = parseFloat(newBill.total || newBill.amount || 0);
    const categoryValue = newBill.terms && categoryMap[newBill.terms] ? categoryMap[newBill.terms] : newBill.terms || "Uncategorized";
    console.log(`Created Supplier Bill ID ${newBill.id} - terms: ${newBill.terms}, mapped category: ${categoryValue}`);
    const mappedBill = {
      id: Number(newBill.id),
      date: formatDateToDDMMYYYY(newBill.date) || getCurrentDate(), // Format date to DD-MM-YYYY
      type: "Bill",
      no: newBill.bill_number || `BILL-${newBill.id}`,
      payee: supplierData?.name || newBill.supplier_name || newBill.supplier || "Unknown",
      category: categoryValue,
      terms: newBill.terms || "",
      totalBeforeTax: billAmount ? (billAmount / 1.17).toFixed(2) : "0.00",
      salesTax: billAmount ? (billAmount * 0.17 / 1.17).toFixed(2) : "0.00",
      total: billAmount,
      status: newBill.status || "Pending",
      address: newBill.address || "No address",
      start_date: formatDateToDDMMYYYY(newBill.start_date) || "", // Format date to DD-MM-YYYY
      end_date: formatDateToDDMMYYYY(newBill.end_date) || "", // Format date to DD-MM-YYYY
      memo: newBill.memo || "No memo",
      supplier: newBill.supplier || id,
      bill_number: newBill.bill_number || `BILL-${newBill.id}`,
      due_date: formatDateToDDMMYYYY(newBill.due_date) || getCurrentDate(), // Format date to DD-MM-YYYY
      file_name: newBill.file_name || (newBill.bill_pdf ? newBill.bill_pdf.split("/").pop() : "Manual Entry"),
      file_path: newBill.file_path || (newBill.bill_pdf ? `${API_BASE_URL}${newBill.bill_pdf}` : ""),
      items: Array.isArray(newBill.items)
        ? newBill.items.map((item) => ({
            id: Date.now() + Math.random(),
            rowNumber: item.rowNumber || "1",
            category: item.category && categoryMap[item.category] ? categoryMap[item.category] : item.category || categoryValue,
            description: item.description || "Initial Item",
            totalAmount: parseFloat(item.totalAmount || billAmount || 0),
          }))
        : [
            {
              id: Date.now() + Math.random(),
              rowNumber: "1",
              category: categoryValue,
              description: newBill.memo || "Initial Item",
              totalAmount: billAmount,
            },
          ],
    };
    setTransactions((prev) => [...prev, mappedBill]);
    setShowCreateBillPopup(false);
    setSelectedBill(null);
  };

  const handleBillUpdated = (updatedBill) => {
    console.log("Updating bill - input data:", updatedBill);
    const billAmount = parseFloat(updatedBill.total || updatedBill.amount || 0);
    const categoryValue = updatedBill.terms && categoryMap[updatedBill.terms] ? categoryMap[updatedBill.terms] : updatedBill.terms || "Uncategorized";
    console.log(`Updated Supplier Bill ID ${updatedBill.id} - terms: ${updatedBill.terms}, mapped category: ${categoryValue}`);
    const mappedBill = {
      id: Number(updatedBill.id),
      date: formatDateToDDMMYYYY(updatedBill.date) || formatDateToDDMMYYYY(updatedBill.paid_date) || getCurrentDate(), // Format date to DD-MM-YYYY
      type: "Bill",
      no: updatedBill.bill_number || `BILL-${updatedBill.id}`,
      payee: supplierData?.name || updatedBill.supplier_name || updatedBill.supplier || "Unknown",
      category: categoryValue,
      terms: updatedBill.terms || "",
      totalBeforeTax: billAmount ? (billAmount / 1.17).toFixed(2) : "0.00",
      salesTax: billAmount ? (billAmount * 0.17 / 1.17).toFixed(2) : "0.00",
      total: billAmount,
      status: updatedBill.status || (updatedBill.paid_date ? "Paid" : "Pending"),
      address: updatedBill.address || "No address",
      start_date: formatDateToDDMMYYYY(updatedBill.start_date) || "", // Format date to DD-MM-YYYY
      end_date: formatDateToDDMMYYYY(updatedBill.end_date) || "", // Format date to DD-MM-YYYY
      memo: updatedBill.memo || "No memo",
      supplier: updatedBill.supplier || id,
      bill_number: updatedBill.bill_number || `BILL-${updatedBill.id}`,
      due_date: formatDateToDDMMYYYY(updatedBill.due_date) || getCurrentDate(), // Format date to DD-MM-YYYY
      file_name: updatedBill.file_name || (updatedBill.bill_pdf ? updatedBill.bill_pdf.split("/").pop() : "Manual Entry"),
      file_path: updatedBill.file_path || (updatedBill.bill_pdf ? `${API_BASE_URL}${updatedBill.bill_pdf}` : ""),
      items: Array.isArray(updatedBill.items)
        ? updatedBill.items.map((item) => ({
            id: Date.now() + Math.random(),
            rowNumber: item.rowNumber || "1",
            category: item.category && categoryMap[item.category] ? categoryMap[item.category] : item.category || categoryValue,
            description: item.description || "Initial Item",
            totalAmount: parseFloat(item.totalAmount || billAmount || 0),
          }))
        : [
            {
              id: Date.now() + Math.random(),
              rowNumber: "1",
              category: categoryValue,
              description: updatedBill.memo || "Initial Item",
              totalAmount: billAmount,
            },
          ],
    };
    setTransactions((prev) => prev.map((bill) => (bill.id === updatedBill.id ? mappedBill : bill)));
    setShowCreateBillPopup(false);
    setSelectedBill(null);
  };

  const handleSaveAndNew = async (expenseData, expenseFile) => {
    await handleExpenseCreated(expenseData, expenseFile);
    setPaymentDate(getCurrentDate());
    setPaymentMethod("");
    setDescription("");
    setCategory("");
    setExpenseFile(null);
    setExpenseTableData([
      {
        id: Date.now(),
        rowNumber: "1",
        category: "General",
        description: "Initial Item",
        totalAmount: "",
      },
    ]);
  };

  const handleClear = () => {
    setPaymentDate(getCurrentDate());
    setPaymentMethod("");
    setDescription("");
    setCategory("");
    setExpenseFile(null);
    setExpenseTableData([
      {
        id: Date.now(),
        rowNumber: "1",
        category: "General",
        description: "Initial Item",
        totalAmount: "",
      },
    ]);
  };

  const handlePrint = () => {
    window.print();
  };

  const handleRecurring = () => {
    alert("Recurring expense creation is not implemented yet.");
  };

  const handleAddRow = () => {
    setExpenseTableData([
      ...expenseTableData,
      {
        id: Date.now(),
        rowNumber: (expenseTableData.length + 1).toString(),
        category: "General",
        description: "New Item",
        totalAmount: "",
      },
    ]);
  };

  const handleDeleteRow = (id) => {
    setExpenseTableData(
      expenseTableData
        .filter((row) => row.id !== id)
        .map((row, index) => ({ ...row, rowNumber: (index + 1).toString() }))
    );
  };

  const handleCopyRow = (id) => {
    const rowToCopy = expenseTableData.find((row) => row.id === id);
    if (rowToCopy) {
      setExpenseTableData([
        ...expenseTableData,
        { ...rowToCopy, id: Date.now(), rowNumber: (expenseTableData.length + 1).toString() },
      ]);
    }
  };

  const handleUpdateRow = (id, field, value) => {
    setExpenseTableData(
      expenseTableData.map((row) =>
        row.id === id
          ? {
              ...row,
              [field]: value,
            }
          : row
      )
    );
  };

  const handleExpenseCreated = async (newExpense, expenseFile) => {
    try {
      const totalAmount = newExpense.items.reduce((sum, item) => sum + (parseFloat(item.totalAmount || "0") || 0), 0) || 0;
      if (isNaN(totalAmount) || totalAmount < 0) {
        throw new Error("Total amount must be a non-negative number.");
      }
      const validItems = (newExpense.items || []).map((item) => ({
        rowNumber: item.rowNumber || "1",
        category: item.category || "General",
        description: item.description || "",
        totalAmount: item.totalAmount || "0",
      }));
      if (validItems.length === 0) {
        validItems.push({
          rowNumber: "1",
          category: newExpense.terms || "General",
          description: newExpense.memo || "Initial Item",
          totalAmount: totalAmount.toString(),
        });
      }
      if (validItems.some((item) => parseFloat(item.totalAmount) < 0)) {
        throw new Error("All item totalAmount values must be non-negative.");
      }
      const formData = new FormData();
      // Convert DD-MM-YYYY to YYYY-MM-DD for API
      const apiDate = newExpense.date ? parseDDMMYYYYToDate(newExpense.date) : getCurrentDateForInput();
      formData.append("date", apiDate);
      formData.append("type", "Expense");
      formData.append("expense_number", newExpense.expense_number || `EXP-${Date.now()}`);
      formData.append("supplier", newExpense.supplier || id);
      formData.append("method", newExpense.method || "Cash");
      formData.append("terms", newExpense.terms || "General");
      formData.append("memo", newExpense.memo || "");
      formData.append("amount", totalAmount.toString());
      formData.append("total", totalAmount.toString());
      formData.append("sales_tax", (totalAmount * 0.17 / 1.17).toFixed(2));
      formData.append("paymentAccount", newExpense.paymentAccount || "Cash on hand");
      formData.append("status", "Paid"); // Set status to "Paid" for new expenses
      formData.append("items", JSON.stringify(validItems));
      if (expenseFile && (!selectedExpense || expenseFile.name !== selectedExpense.file_name)) {
        formData.append("file", expenseFile);
      }
      let response;
      if (selectedExpense) {
        response = await axios.put(`${API_BASE_URL}/api/expenses/${selectedExpense.id}/`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        response = await axios.post(`${API_BASE_URL}/api/expenses/`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      const createdExpense = response.data;
      console.log("Server response for expense:", createdExpense);
      const itemsTotal = createdExpense.items
        ? createdExpense.items.reduce((sum, item) => sum + (parseFloat(item.totalAmount) || 0), 0)
        : parseFloat(createdExpense.total?.replace("£", "") || totalAmount);
      let expenseAmount = isNaN(itemsTotal) ? totalAmount : itemsTotal;
      if (isNaN(expenseAmount) || expenseAmount < 0) {
        console.warn("Invalid expense amount in server response, using input totalAmount:", totalAmount);
        expenseAmount = totalAmount;
      }
      const categoryValue = categoryMap[createdExpense.terms] || createdExpense.terms || newExpense.terms || "General";
      const mappedExpense = {
        id: createdExpense.id,
        date: formatDateToDDMMYYYY(createdExpense.date) || newExpense.date || getCurrentDate(), // Format date to DD-MM-YYYY
        type: "Expense",
        no: createdExpense.no || createdExpense.expense_number || newExpense.expense_number || `EXP-${createdExpense.id}`,
        payee: supplierData?.name || createdExpense.supplier || createdExpense.payee || newExpense.supplier || "Unknown",
        category: categoryValue,
        terms: createdExpense.terms || newExpense.terms || "General",
        totalBeforeTax: (expenseAmount / 1.17).toFixed(2),
        salesTax: (expenseAmount * 0.17 / 1.17).toFixed(2),
        total: expenseAmount,
        status: "Paid", // Ensure status is "Paid" for created/updated expenses
        address: createdExpense.address || "",
        start_date: "",
        end_date: "",
        memo: createdExpense.memo || newExpense.memo || "",
        supplier: createdExpense.supplier || newExpense.supplier || id,
        expense_number: createdExpense.no || createdExpense.expense_number || newExpense.expense_number || `EXP-${createdExpense.id}`,
        due_date: formatDateToDDMMYYYY(createdExpense.due_date) || getCurrentDate(), // Format date to DD-MM-YYYY
        file_name: createdExpense.file_name || (createdExpense.file_path ? createdExpense.file_path.split("/").pop() : "Manual Entry"),
        file_path: createdExpense.file_path ? `${API_BASE_URL}${createdExpense.file_path}` : "",
        method: createdExpense.method || newExpense.method || "",
        paymentAccount: createdExpense.paymentAccount || newExpense.paymentAccount || "Cash on hand",
        items: Array.isArray(createdExpense.items)
          ? createdExpense.items.map((item) => ({
              id: Date.now() + Math.random(),
              rowNumber: item.rowNumber || "1",
              category: categoryMap[item.category] || item.category || "General",
              description: item.description || "Initial Item",
              totalAmount: item.totalAmount || "0",
            }))
          : validItems.length > 0
            ? validItems.map((item) => ({
                id: Date.now() + Math.random(),
                rowNumber: item.rowNumber,
                category: categoryMap[item.category] || item.category || "General",
                description: item.description || "Initial Item",
                totalAmount: item.totalAmount || "0",
              }))
            : [
                {
                  id: Date.now() + Math.random(),
                  rowNumber: "1",
                  category: categoryValue,
                  description: createdExpense.memo || newExpense.memo || "Initial Item",
                  totalAmount: expenseAmount.toString(),
                },
              ],
      };
      setTransactions((prev) =>
        selectedExpense
          ? prev.map((trans) => (trans.id === mappedExpense.id ? mappedExpense : trans))
          : [...prev, mappedExpense]
      );
      setShowCreateExpensePopup(false);
      setSelectedExpense(null);
      setPaymentDate(getCurrentDate());
      setPaymentMethod("");
      setDescription("");
      setCategory("");
      setExpenseFile(null);
      setExpenseTableData([
        {
          id: Date.now(),
          rowNumber: "1",
          category: "General",
          description: "Initial Item",
          totalAmount: "",
        },
      ]);
      alert(selectedExpense ? "Expense updated successfully!" : "Expense created successfully!");
    } catch (error) {
      const errorMessage = `Failed to save expense: ${error.message}${
        error.response?.data ? ` - ${JSON.stringify(error.response.data)}` : ""
      }`;
      setError(errorMessage);
      alert(errorMessage);
    }
  };

  const handleClearAllLines = () => {
    setExpenseTableData([
      {
        id: Date.now(),
        rowNumber: "1",
        category: "General",
        description: "Initial Item",
        totalAmount: "",
      },
    ]);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.name.endsWith(".pdf")) {
      if (file.size > 20 * 1024 * 1024) {
        alert("File size exceeds 20MB limit.");
        return;
      }
      setExpenseFile(file);
    } else {
      alert("Please select a PDF file.");
    }
  };

  const handleSearch = (e) => setSearchQuery(e.target.value);
  const handleSortChange = (e) => setSortOrder(e.target.value);
  const toggleDropdown = () => setIsDropdownOpen((prev) => !prev);

  if (error) return <div className="error-message">Error: {error}</div>;

  return (
    <div className="supplier-details-container">
      <h1 className="page-title">Expenses</h1>
      <nav className="navbar">
        <NavLink to="/admin/pk-expenses" className={({ isActive }) => `nav-button${isActive ? " active" : ""}`}>
          Expenses
        </NavLink>
        <NavLink to="/admin/bills" className={({ isActive }) => `nav-button${isActive ? " active" : ""}`}>
          Bills
        </NavLink>
        <NavLink to="/admin/pk-suppliers" className={({ isActive }) => `nav-button${isActive ? " active" : ""}`}>
          Suppliers
        </NavLink>
      </nav>
      <div className="header-actions">
        <div className="suppliers-title">Suppliers</div>
        <div>
          <button className="edit-button" onClick={() => navigate(`/admin/pk-suppliers`)}>
            Edit
          </button>
          <div className="dropdown-container" ref={dropdownRef}>
            <button className="new-transaction-button" onClick={toggleDropdown}>
              New transaction
            </button>
            {isDropdownOpen && (
              <div className="dropdown-menus">
                <button className="dropdown-items" onClick={handleCreateBillClick}>
                  Bill
                </button>
                <button className="dropdown-items" onClick={handleCreateExpenseClick}>
                  Expense
                </button>
                <NavLink to="/admin/new-cheque" className="dropdown-item" onClick={() => setIsDropdownOpen(false)}>
                  Cheque
                </NavLink>
                <NavLink
                  to="/admin/new-purchase-order"
                  className="dropdown-item"
                  onClick={() => setIsDropdownOpen(false)}
                >
                  Purchase Order
                </NavLink>
                <NavLink
                  to="/admin/new-supplier-credit"
                  className="dropdown-item"
                  onClick={() => setIsDropdownOpen(false)}
                >
                  Supplier Credit
                </NavLink>
                <NavLink to="/admin/import-bills" className="dropdown-item" onClick={() => setIsDropdownOpen(false)}>
                  Import Bills
                </NavLink>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="main-layout">
        <div className="sidebar">
          <div className="sidebar-header-icons">
            <FiPlus size={20} />
            <FiMenu size={20} />
          </div>
          <p className="sidebar-label">Filter by name or details</p>
          <input
            type="text"
            placeholder="Search..."
            className="filter-input"
            value={searchQuery}
            onChange={handleSearch}
          />
          <select className="sort-dropdown" value={sortOrder} onChange={handleSortChange}>
            <option value="name-asc">Sort by name (A-Z)</option>
            <option value="name-desc">Sort by name (Z-A)</option>
          </select>
          {loading && <p>Loading suppliers...</p>}
          <div className="supplier-lists">
            {suppliers.length === 0 && !loading ? (
              <p>No suppliers found</p>
            ) : (
              suppliers.map((supplier) => (
                <div
                  key={supplier.id}
                  className={`supplier-item ${id === supplier.id.toString() ? "active" : ""}`}
                >
                  <NavLink to={`/admin/supplier-details/${supplier.id}`} className="custom-navlink">
                    {supplier.supplier_display_name || `${supplier.first_name} ${supplier.last_name}`}
                  </NavLink>
                  <hr />
                </div>
              ))
            )}
          </div>
        </div>
        <div className="content-panel">
          {loading && <p>Loading supplier details...</p>}
          {supplierData && (
            <>
              <div className="info-summary-row">
                <div className="supplier-info-panel">
                  <h2>
                    {supplierData.name} <span className="contact-info"><FaEnvelopeOpen /><FaPhoneAlt /></span>
                  </h2>
                  <p className="company-name">{supplierData.company_name}</p>
                  <p className="address">Billing address</p>
                  <p className="address">{supplierData.billingAddress || "N/A"}</p>
                  <p className="notes">Notes</p>
                  <p className="notes"><button className="add-notes">Add notes</button></p>
                </div>
                <div className="summary-panel">
                  <h2>SUMMARY <span className="summary-note">(Bill Pay ACH Info)</span></h2>
                  {error && error.includes("transactions") ? (
                    <p>Error loading summary data</p>
                  ) : (
                    <div className="summary-items">
                      <div className="summary-item">
                        <span className="summary-label paid-label">Paid Last 30 Days</span>
                        <span className="summary-value">
                          {formatPrice(summary.paidLast30Days)}
                        </span>
                      </div>
                      <div className="summary-item">
                        <span className="summary-label open-balance-label">Open Balance</span>
                        <span className="summary-value">
                          {formatPrice(summary.openBalance)}
                        </span>
                      </div>
                      <div className="summary-item">
                        <span className="summary-label overdue-label">Overdue</span>
                        <span className="summary-value">
                          {formatPrice(summary.overdue)}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="action-button-row">
                <button
                  className={`mid-action-button ${!showSupplierDetails ? "active" : ""}`}
                  onClick={() => setShowSupplierDetails(false)}
                >
                  Transaction List
                </button>
                <button
                  className={`actions-btn ${showSupplierDetails ? "active" : ""}`}
                  onClick={() => setShowSupplierDetails(true)}
                >
                  Supplier Details
                </button>
              </div>
              {showSupplierDetails && (
                <div className="supplier-details-view">
                  <div className="supplier-details-content">
                    <div className="details-section">
                      <h3>Contact info</h3>
                      <div><strong>Supplier:</strong> {supplierData.company_name || "N/A"}</div>
                      <div><strong>Email:</strong> {supplierData.email || "N/A"}</div>
                      <div><strong>Phone:</strong> {supplierData.phone_number || "N/A"}</div>
                      <div><strong>Mobile:</strong> {supplierData.mobile_number || "-"}</div>
                      <div><strong>Fax:</strong> {supplierData.fax || "-"}</div>
                      <div><strong>Other:</strong> {supplierData.other || "-"}</div>
                      <div>
                        <strong>Website:</strong>{" "}
                        {supplierData.website ? (
                          <a href={supplierData.website} target="_blank" rel="noopener noreferrer">
                            {supplierData.website}
                          </a>
                        ) : (
                          "-"
                        )}
                      </div>
                    </div>
                    <div className="details-section">
                      <h3>Additional info</h3>
                      <div><strong>Bill Pay ACH Info:</strong> -</div>
                      <div><strong>Billing address:</strong> {supplierData.billingAddress || "N/A"}</div>
                      <div><strong>Terms:</strong> -</div>
                      <div><strong>Company:</strong> {supplierData.company_name || "N/A"}</div>
                      <div><strong>Notes:</strong> -</div>
                    </div>
                  </div>
                  <div className="attachments-section">
                    <h3>Attachments</h3>
                    <div className="attachment-dropzone">
                      <span>Add attachment</span>
                      <div>Max file size: 20 MB</div>
                    </div>
                  </div>
                </div>
              )}
              {showCreateBillPopup && (
                <div className="modal-overlay" style={{ zIndex: 1001 }}>
                  <CreateBillPopup
                    supplierId={id}
                    supplierData={supplierData}
                    billData={selectedBill}
                    onClose={() => {
                      setShowCreateBillPopup(false);
                      setSelectedBill(null);
                    }}
                    onBillCreated={handleBillCreated}
                    onBillUpdated={handleBillUpdated}
                  />
                </div>
              )}
              {showCreateExpensePopup && (
                <SupplierExpensePopup
                  showCreateExpensePopup={showCreateExpensePopup}
                  setShowCreateExpensePopup={setShowCreateExpensePopup}
                  supplierData={supplierData}
                  paymentAccount={paymentAccount}
                  setPaymentAccount={setPaymentAccount}
                  paymentDate={paymentDate}
                  setPaymentDate={setPaymentDate}
                  paymentMethod={paymentMethod}
                  setPaymentMethod={setPaymentMethod}
                  description={description}
                  setDescription={setDescription}
                  category={category}
                  setCategory={setCategory}
                  expenseFile={expenseFile}
                  setExpenseFile={setExpenseFile}
                  tableData={expenseTableData}
                  setTableData={setExpenseTableData}
                  totalAmount={expenseTableData.reduce((sum, row) => sum + (parseFloat(row.totalAmount) || 0), 0)}
                  handleExpenseCreated={handleExpenseCreated}
                  handleSaveAndNew={handleSaveAndNew}
                  handleClear={handleClear}
                  handlePrint={handlePrint}
                  handleRecurring={handleRecurring}
                  handleAddRow={handleAddRow}
                  handleDeleteRow={handleDeleteRow}
                  handleCopyRow={handleCopyRow}
                  handleUpdateRow={handleUpdateRow}
                  handleClearAllLines={handleClearAllLines}
                  handleFileChange={handleFileChange}
                  selectedExpense={selectedExpense}
                />
              )}
              {showChangeCategoryModal && (
                <div className="modal-overlay" style={{ zIndex: 1001 }}>
                  <div className="modal-content" ref={changeCategoryModalRef}>
                    <h2>Change Category</h2>
                    <select
                      value={newCategory}
                      onChange={(e) => setNewCategory(e.target.value)}
                      className="modal-select"
                    >
                      <option value="">Select Category</option>
                      {categories.map((cat) => (
                        <option key={cat.id} value={cat.name}>
                          {cat.display_name}
                        </option>
                      ))}
                    </select>
                    <div className="modal-buttons">
                      <button
                        onClick={() => {
                          setShowChangeCategoryModal(false);
                          setNewCategory("");
                        }}
                        className="modal-cancel-button"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleChangeCategory}
                        className="modal-save-button"
                        disabled={!newCategory}
                      >
                        Save
                      </button>
                    </div>
                  </div>
                </div>
              )}
              {showDeleteModal && (
                <div className="modal-overlay" style={{ zIndex: 1001 }}>
                  <div className="modal-content" ref={deleteModalRef}>
                    <h2>Confirm Deletion</h2>
                    <p>Are you sure you want to delete {selectedTransactionIds.length} bill(s)? They will be marked as deleted and removed from this page.</p>
                    <div className="modal-buttons">
                      <button
                        onClick={() => setShowDeleteModal(false)}
                        className="modal-cancel-button"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleDeleteBill}
                        className="modal-delete-button"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              )}
              {!showSupplierDetails && (
                <div className="transaction-section">
                  {onlyBillsSelected() && (
                    <div className="actions-topbar">
                      <span className="actions-topbar-label">What do you want to do?</span>
                      <button
                        className="actions-topbar-button"
                        onClick={handleOpenChangeCategoryModal}
                      >
                        Change Category
                      </button>
                      <button
                        className="actions-topbar-button actions-topbar-button-delete"
                        onClick={() => setShowDeleteModal(true)}
                      >
                        Delete
                      </button>
                    </div>
                  )}
                  <div className="filter-section">
                    <div
                      className="filter-toggle"
                      onClick={() => setShowFilterModal(!showFilterModal)}
                      ref={filterBtnRef}
                    >
                      <FiFilter />
                      <span>Filter</span>
                    </div>
                    {showFilterModal && (
                      <div
                        className="filters-dropdown"
                        ref={filterModalRef}
                        style={{
                          position: "absolute",
                          top: filterBtnRef.current ? filterBtnRef.current.offsetTop + filterBtnRef.current.offsetHeight : 0,
                          left: filterBtnRef.current ? filterBtnRef.current.offsetLeft : 0,
                          marginTop: "0",
                          zIndex: 1000,
                        }}
                      >
                        <button className="dropdown-close-btn" onClick={() => setShowFilterModal(false)}>
                          <FaTimes />
                        </button>
                        <div className="filter-options">
                          <div className="filter-group">
                            <label className="filter-label">Dates</label>
                            <div className="filter-input-row">
                              <select
                                className="filter-select"
                                value={dateFilter}
                                onChange={(e) => setDateFilter(e.target.value)}
                              >
                                <option value="All dates">All dates</option>
                                <option value="Custom">Custom</option>
                                <option value="Since 365 Days ago">Since 365 Days ago</option>
                                <option value="This Month">This Month</option>
                                <option value="This Quarter">This Quarter</option>
                                <option value="This Year">This Year</option>
                                <option value="Last Month">Last Month</option>
                                <option value="Last Quarter">Last Quarter</option>
                                <option value="Last Year">Last Year</option>
                              </select>
                              {dateFilter === "Custom" && (
                                <>
                                  <div className="filter-date-input">
                                    <label className="filter-sublabel">From</label>
                                    <input
                                      type="date"
                                      value={customFromDate}
                                      onChange={(e) => setCustomFromDate(e.target.value)}
                                      className="filter-input"
                                    />
                                  </div>
                                  <div className="filter-date-input">
                                    <label className="filter-sublabel">To</label>
                                    <input
                                      type="date"
                                      value={customToDate}
                                      onChange={(e) => setCustomToDate(e.target.value)}
                                      className="filter-input"
                                    />
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                          <div className="filter-group">
                            <label className="filter-label">Transaction type</label>
                            <select
                              className="filter-select"
                              value={transactionType}
                              onChange={(e) => setTransactionType(e.target.value)}
                            >
                              <option value="All transactions">All transactions</option>
                              <option value="Bill">Bill</option>
                              <option value="Expense">Expense</option>
                            </select>
                          </div>
                          <div className="filter-group">
                            <label className="filter-label">Category</label>
                            <select
                              className="filter-select"
                              value={categoryFilter}
                              onChange={(e) => setCategoryFilter(e.target.value)}
                            >
                              <option value="">All categories</option>
                              {categories.map((cat) => (
                                <option key={cat.id} value={cat.display_name}>
                                  {cat.display_name}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="filter-group">
                            <label className="filter-label">Amount</label>
                            <div className="filter-input-row">
                              <select
                                className="filter-select"
                                value={amountOperator}
                                onChange={(e) => setAmountOperator(e.target.value)}
                              >
                                <option value="Between">Between</option>
                                <option value="Less than">Less than</option>
                                <option value="Greater than">Greater than</option>
                                <option value="Equals">Equals</option>
                              </select>
                              <input
                                type="number"
                                step="0.01"
                                placeholder="Min"
                                value={minAmount}
                                onChange={(e) => setMinAmount(e.target.value)}
                                className="filter-input"
                              />
                              {amountOperator === "Between" && (
                                <input
                                  type="number"
                                  step="0.01"
                                  placeholder="Max"
                                  value={maxAmount}
                                  onChange={(e) => setMaxAmount(e.target.value)}
                                  className="filter-input"
                                />
                              )}
                            </div>
                          </div>
                          <div className="filter-buttons">
                            <button
                              className="filter-apply-button"
                              onClick={() => setShowFilterModal(false)}
                            >
                              Apply
                            </button>
                            <button
                              className="filter-reset-button"
                              onClick={() => {
                                handleResetFilters();
                                setShowFilterModal(false);
                              }}
                            >
                              Reset
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  <table className="transaction-table" key={filteredTransactions.length}>
                    <thead>
                      <tr>
                        <th style={{ width: "5%" }}></th>
                        <th style={{ width: "10%" }}>Date</th>
                        <th style={{ width: "10%" }}>Type</th>
                        <th style={{ width: "10%" }}>No</th>
                        <th style={{ width: "15%" }}>Payee</th>
                        <th style={{ width: "15%" }}>Category</th>
                        <th style={{ width: "10%" }}>Total Before Tax</th>
                        <th style={{ width: "10%" }}>Sales Tax</th>
                        <th style={{ width: "10%" }}>Total</th>
                        <th style={{ width: "10%" }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredTransactions.length === 0 ? (
                        <tr>
                          <td colSpan="10" style={{ textAlign: "center", padding: "20px" }}>
                            No transactions found
                          </td>
                        </tr>
                      ) : (
                        filteredTransactions.map((transaction) => (
                          <tr
                            key={transaction.id}
                            onClick={() => handleTransactionClick(transaction)}
                            className="transaction-row"
                          >
                            <td>
                              <input
                                type="checkbox"
                                checked={selectedTransactionIds.includes(transaction.id)}
                                onChange={() => handleCheckboxChange(transaction.id)}
                                onClick={(e) => e.stopPropagation()}
                              />
                            </td>
                            <td>{transaction.date || "-"}</td>
                            <td>{transaction.type || "-"}</td>
                            <td>{transaction.no || "-"}</td>
                            <td>{transaction.payee || supplierData?.name || "-"}</td>
                            <td>{transaction.category || "-"}</td>
                            <td>{formatPrice(transaction.totalBeforeTax)}</td>
                            <td>{formatPrice(transaction.salesTax)}</td>
                            <td>{formatPrice(transaction.total)}</td>
                            <td>
                              {transaction.type === "Bill" && ["Pending", "Unpaid"].includes(transaction.status) ? (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleTransactionClick(transaction);
                                  }}
                                  style={{
                                    background: "none",
                                    border: "none",
                                    color: "#004aad",
                                    textDecoration: "none",
                                    cursor: "pointer",
                                    padding: 0,
                                    fontSize: "inherit",
                                  }}
                                >
                                  Mark as Paid
                                </button>
                              ) : (
                                transaction.status || "-"
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SuppliersDetailPage;